import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { Plus, Edit, Trash2, X, Users, Building2, Search, Mail, Phone, Shield, ChevronDown, Check, Key, Eye, EyeOff, CheckCircle, Download, Upload, FileText, AlertTriangle, Loader2, Crown, Clock, UserMinus } from 'lucide-react';
import OffboardingWizard from './OffboardingWizard';
import { getTodayLocal } from '../lib/dateUtils';
import MobileDataCard from './MobileDataCard';
import EmployeeDataGrid from './EmployeeDataGrid';

// Currency options
const currencies = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' }
];

// Calculate tenure from first_working_day - returns compact format like "4y 10m 11d"
const calculateTenure = (firstWorkingDay) => {
  if (!firstWorkingDay) return null;
  
  const start = new Date(firstWorkingDay);
  const now = new Date();
  
  // Handle future dates
  if (start > now) return 'Not started';
  
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  
  // Adjust for negative days
  if (days < 0) {
    months--;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }
  
  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }
  
  // Build compact tenure string
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0 || parts.length === 0) parts.push(`${days}d`);
  
  return parts.join(' ');
};

// Multi-Select Checkbox Dropdown for Departments
// NOTE: Uses department NAMES for selection (not ObjectIds) to match backend storage
function DepartmentMultiSelect({ departments, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleDepartment = (deptName) => {
    if (selected.includes(deptName)) {
      onChange(selected.filter(name => name !== deptName));
    } else {
      onChange([...selected, deptName]);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 text-left border border-white/40 rounded-xl bg-white flex items-center justify-between ${isOpen ? 'ring-2 ring-pink-500' : ''}`}
      >
        <span className={selected.length > 0 ? 'text-[#1a1a2e]' : 'text-neutral-400'}>
          {selected.length > 0 ? `${selected.length} department(s) selected` : 'Select departments'}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full mt-1 glass-card rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {departments.map(dept => (
              <button
                key={dept._id}
                type="button"
                onClick={() => toggleDepartment(dept.name)}
                className="w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-pink-50"
              >
                <span className={`w-5 h-5 border-2 rounded flex items-center justify-center ${selected.includes(dept.name) ? 'bg-pink-500 border-pink-500' : 'border-white/40'}`}>
                  {selected.includes(dept.name) && <Check className="w-3 h-3 text-white" />}
                </span>
                <span>{dept.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Add/Edit Employee Modal
function EmployeeModal({ isOpen, onClose, onSubmit, departments, existingEmployee = null, designationLevels = [] }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    employee_id: '',
    ctc_amount: '',
    ctc_currency: 'INR',
    designation: '',
    designation_level: '',
    departments: [],
    work_email: '',
    mobile: '',
    address: '',
    gender: '',
    date_of_birth: '',
    aadhar_number: '',
    pan_number: '',
    bank_account_number: '',
    bank_name: '',
    bank_ifsc: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
    first_working_day: '',
    last_working_day: '',
    farewell_message: ''
  });
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);

  useEffect(() => {
    if (existingEmployee) {
      // Parse full_name into first_name and last_name if they're not set
      let firstName = existingEmployee.personal?.first_name || '';
      let lastName = existingEmployee.personal?.last_name || '';
      
      // If first_name/last_name are empty but full_name exists, parse it
      if (!firstName && !lastName && existingEmployee.personal?.full_name) {
        const nameParts = existingEmployee.personal.full_name.trim().split(' ');
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' '); // Handle names like "Mary Jane Watson"
        } else if (nameParts.length === 1) {
          firstName = nameParts[0];
          lastName = '';
        }
      }
      
      setFormData({
        first_name: firstName,
        last_name: lastName,
        employee_id: existingEmployee.employee_id || existingEmployee.employment?.employee_id || '',
        ctc_amount: existingEmployee.ctc?.amount || existingEmployee.employment?.ctc?.amount || '',
        ctc_currency: existingEmployee.ctc?.currency || existingEmployee.employment?.ctc?.currency || 'INR',
        designation: existingEmployee.designation || existingEmployee.employment?.designation || '',
        designation_level: existingEmployee.designation_level || existingEmployee.employment?.designation_level || '',
        departments: existingEmployee.departments || existingEmployee.employment?.departments || [],
        work_email: existingEmployee.work_email || '',
        mobile: existingEmployee.personal?.mobile || '',
        address: existingEmployee.personal?.address || '',
        gender: existingEmployee.personal?.gender || '',
        date_of_birth: existingEmployee.personal?.date_of_birth || '',
        aadhar_number: existingEmployee.personal?.aadhar_number || '',
        pan_number: existingEmployee.personal?.pan_number || '',
        bank_account_number: existingEmployee.bank?.account_number || '',
        bank_name: existingEmployee.bank?.bank_name || '',
        bank_ifsc: existingEmployee.bank?.ifsc || '',
        emergency_contact_name: existingEmployee.emergency?.name || '',
        emergency_contact_number: existingEmployee.emergency?.phone || '',
        first_working_day: existingEmployee.employment?.first_working_day || existingEmployee.employment?.start_date || '',
        last_working_day: existingEmployee.employment?.last_working_day || existingEmployee.employment?.last_day || '',
        farewell_message: existingEmployee.employment?.farewell_message || ''
      });
    } else {
      // For new employees, default first_working_day to today
      const today = getTodayLocal();
      setFormData({
        first_name: '',
        last_name: '',
        employee_id: '',
        ctc_amount: '',
        ctc_currency: 'INR',
        designation: '',
        designation_level: '',
        departments: [],
        work_email: '',
        mobile: '',
        address: '',
        gender: '',
        date_of_birth: '',
        aadhar_number: '',
        pan_number: '',
        bank_account_number: '',
        bank_name: '',
        bank_ifsc: '',
        emergency_contact_name: '',
        emergency_contact_number: '',
        first_working_day: today,
        last_working_day: '',
        farewell_message: ''
      });
    }
  }, [existingEmployee, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Per-field validation — call out the EXACT missing field instead of a generic
    // "fill all required fields" toast (which was misleading users into thinking
    // First Working Day was the culprit when it wasn't).
    const missing = [];
    if (!formData.first_name)        missing.push('First Name');
    if (!formData.last_name)         missing.push('Last Name');
    if (!formData.employee_id)       missing.push('Employee ID');
    if (!formData.ctc_amount)        missing.push('CTC Amount');
    if (!formData.designation)       missing.push('Designation');
    if (!formData.work_email)        missing.push('Work Email');
    if (!formData.departments || formData.departments.length === 0) missing.push('Department(s)');
    if (!formData.first_working_day) missing.push('First Working Day');
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.join(', ')}`);
      return;
    }
    
    onSubmit({
      employee_id: formData.employee_id,
      ctc: {
        amount: parseFloat(formData.ctc_amount),
        currency: formData.ctc_currency
      },
      designation: formData.designation,
      designation_level: formData.designation_level || null,
      departments: formData.departments,
      work_email: formData.work_email,
      gender: formData.gender || null,
      date_of_birth: formData.date_of_birth || null,
      first_working_day: formData.first_working_day,
      last_working_day: formData.last_working_day || null,
      farewell_message: formData.farewell_message || null,
      personal: {
        first_name: formData.first_name,
        last_name: formData.last_name,
        full_name: `${formData.first_name} ${formData.last_name}`,
        mobile: formData.mobile,
        address: formData.address,
        gender: formData.gender || null,
        date_of_birth: formData.date_of_birth || null,
        aadhar_number: formData.aadhar_number,
        pan_number: formData.pan_number
      },
      bank: {
        account_number: formData.bank_account_number,
        bank_name: formData.bank_name,
        ifsc: formData.bank_ifsc
      },
      emergency: {
        name: formData.emergency_contact_name,
        phone: formData.emergency_contact_number
      }
    });
  };

  if (!isOpen) return null;

  const currencySymbol = currencies.find(c => c.code === formData.ctc_currency)?.symbol || '₹';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/40">
          <div>
            <h2 className="text-xl font-bold text-[#1a1a2e]">{existingEmployee ? 'Edit Employee' : 'Add Employee'}</h2>
            <p className="text-sm text-neutral-500 mt-1">Enter employee details</p>
          </div>
          <button onClick={onClose} className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Required Fields Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-[#1a1a2e] text-sm uppercase tracking-wide">Required Information</h3>
            
            {/* First Name and Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="John"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Employee ID *</label>
                <input
                  type="text"
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="EMP001"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">CTC (Annual) *</label>
                <div className="flex gap-2">
                  <select
                    value={formData.ctc_currency}
                    onChange={(e) => setFormData({ ...formData, ctc_currency: e.target.value })}
                    className="px-3 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 bg-white"
                  >
                    {currencies.map(c => (
                      <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={formData.ctc_amount}
                    onChange={(e) => setFormData({ ...formData, ctc_amount: e.target.value })}
                    className="flex-1 px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                    placeholder="500000"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Designation *</label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="Senior Designer"
                  required
                  data-testid="employee-designation-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  <span className="flex items-center gap-1.5">
                    <Crown className="w-4 h-4 text-amber-500" />
                    Hierarchy Level
                  </span>
                </label>
                <select
                  value={formData.designation_level}
                  onChange={(e) => setFormData({ ...formData, designation_level: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 bg-white"
                  data-testid="employee-designation-level-select"
                >
                  <option value="">Select Level</option>
                  {designationLevels.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-neutral-500 mt-1">Company hierarchy for permissions</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Department(s) *</label>
              <DepartmentMultiSelect
                departments={departments}
                selected={formData.departments}
                onChange={(depts) => setFormData({ ...formData, departments: depts })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Email ID *</label>
              <input
                type="email"
                value={formData.work_email}
                onChange={(e) => setFormData({ ...formData, work_email: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                placeholder="john@halfeatendonut.in"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30 bg-white"
                data-testid="employee-gender-select"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            
            {/* Date of Birth - Important for birthday wishes */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Date of Birth</label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                data-testid="date-of-birth-input"
              />
              <p className="text-xs text-neutral-500 mt-1">For birthday wishes on Notice Board 🎂</p>
            </div>
            
            {/* Employment Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">First Working Day *</label>
                <input
                  type="date"
                  value={formData.first_working_day}
                  onChange={(e) => setFormData({ ...formData, first_working_day: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  required
                  data-testid="first-working-day-input"
                />
                <p className="text-xs text-neutral-500 mt-1">Employee&apos;s joining date</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Last Working Day</label>
                <input
                  type="date"
                  value={formData.last_working_day}
                  onChange={(e) => setFormData({ ...formData, last_working_day: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  data-testid="last-working-day-input"
                />
                <p className="text-xs text-neutral-500 mt-1">Leave empty if not departing</p>
              </div>
            </div>
            
            {/* Farewell Message - only show if last working day is set */}
            {formData.last_working_day && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Farewell Message</label>
                <input
                  type="text"
                  value={formData.farewell_message}
                  onChange={(e) => setFormData({ ...formData, farewell_message: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="Off to new adventures! 🚀"
                  data-testid="farewell-message-input"
                />
                <p className="text-xs text-neutral-500 mt-1">Custom message for notice board (optional)</p>
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-[#1a1a2e] text-sm uppercase tracking-wide">Contact Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="+91 98765 43210"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="City, State"
                />
              </div>
            </div>
          </div>

          {/* Identity Documents */}
          <div className="space-y-4">
            <h3 className="font-semibold text-[#1a1a2e] text-sm uppercase tracking-wide">Identity Documents</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Aadhar Number</label>
                <input
                  type="text"
                  value={formData.aadhar_number}
                  onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="XXXX XXXX XXXX"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">PAN Number</label>
                <input
                  type="text"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="ABCDE1234F"
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-[#1a1a2e] text-sm uppercase tracking-wide">Bank Account Details</h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Account Number</label>
                <input
                  type="text"
                  value={formData.bank_account_number}
                  onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="Account Number"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Bank Name</label>
                <input
                  type="text"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="Bank Name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">IFSC Code</label>
                <input
                  type="text"
                  value={formData.bank_ifsc}
                  onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="IFSC Code"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="space-y-4">
            <h3 className="font-semibold text-[#1a1a2e] text-sm uppercase tracking-wide">Emergency Contact</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="Emergency Contact Name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Contact Number</label>
                <input
                  type="tel"
                  value={formData.emergency_contact_number}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_number: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-4 p-6 border-t border-white/40 bg-white/40">
          <button type="button" onClick={onClose} className="px-6 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 font-medium">
            {existingEmployee ? 'Update Employee' : 'Add Employee'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add/Edit Department Modal
function DepartmentModal({ isOpen, onClose, onSubmit, employees, existingDepartment = null }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    employee_ids: []
  });

  useEffect(() => {
    if (existingDepartment) {
      setFormData({
        name: existingDepartment.name || '',
        description: existingDepartment.description || '',
        employee_ids: existingDepartment.employee_ids || []
      });
    } else {
      setFormData({ name: '', description: '', employee_ids: [] });
    }
  }, [existingDepartment, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter department name');
      return;
    }
    onSubmit(formData);
  };

  const toggleEmployee = (empId) => {
    if (formData.employee_ids.includes(empId)) {
      setFormData({ ...formData, employee_ids: formData.employee_ids.filter(id => id !== empId) });
    } else {
      setFormData({ ...formData, employee_ids: [...formData.employee_ids, empId] });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/40">
          <h2 className="text-xl font-bold text-[#1a1a2e]">{existingDepartment ? 'Edit Department' : 'Create Department'}</h2>
          <button onClick={onClose} className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Department Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
              placeholder="Design"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
              rows={3}
              placeholder="Department description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Department Members</label>
            <div className="border border-white/40 rounded-xl max-h-48 overflow-y-auto">
              {employees.length === 0 ? (
                <p className="p-4 text-neutral-500 text-sm text-center">No employees available</p>
              ) : (
                employees.map(emp => (
                  <button
                    key={emp._id}
                    type="button"
                    onClick={() => toggleEmployee(emp._id)}
                    className="w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-pink-50 border-b border-white/30 last:border-b-0"
                  >
                    <span className={`w-5 h-5 border-2 rounded flex items-center justify-center ${formData.employee_ids.includes(emp._id) ? 'bg-pink-500 border-pink-500' : 'border-white/40'}`}>
                      {formData.employee_ids.includes(emp._id) && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <div>
                      <p className="font-medium text-[#1a1a2e]">{emp.personal?.full_name || emp.work_email}</p>
                      <p className="text-xs text-neutral-500">{emp.designation || 'No designation'}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-4 p-6 border-t border-white/40 bg-white/40">
          <button type="button" onClick={onClose} className="px-6 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 font-medium">
            {existingDepartment ? 'Update Department' : 'Create Department'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reset Password Modal
function ResetPasswordModal({ isOpen, onClose, employee, onSuccess }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await adminApi.post(`/api/admin/employees/${employee._id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${employee.work_email}`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white  w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-white/40">
          <div>
            <h2 className="text-xl font-bold text-[#1a1a2e]">Reset Password</h2>
            <p className="text-sm text-neutral-500 mt-1">{employee?.work_email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-xl">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
              placeholder="Confirm new password"
              data-testid="confirm-password-input"
            />
          </div>

          <p className="text-xs text-neutral-500">
            Password must be at least 6 characters long.
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Key className="w-4 h-4" />
              )}
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminOrganisationPage() {
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designationLevels, setDesignationLevels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordEmployee, setResetPasswordEmployee] = useState(null);
  
  // Offboarding state
  const [showOffboardingWizard, setShowOffboardingWizard] = useState(false);
  const [offboardingEmployee, setOffboardingEmployee] = useState(null);
  
  // Import/Export state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // 'upload', 'preview', 'applying', 'complete'
  const [importPreview, setImportPreview] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedImportRows, setSelectedImportRows] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchDesignationLevels();
  }, []);

  const fetchEmployees = async () => {
    try {
      const r = await adminApi.get('/api/admin/employees');
      setEmployees(r.data || []);
    } catch (e) {
      console.error('Error fetching employees:', e);
    }
  };

  const fetchDepartments = async () => {
    try {
      const r = await adminApi.get('/api/departments');
      setDepartments(r.data || []);
    } catch (e) {
      console.error('Error fetching departments:', e);
    }
  };

  const fetchDesignationLevels = async () => {
    try {
      const r = await adminApi.get('/api/admin/designation-levels');
      setDesignationLevels(r.data?.designation_levels || []);
    } catch (e) {
      console.error('Error fetching designation levels:', e);
    }
  };

  const handleCreateEmployee = async (data) => {
    try {
      await adminApi.post('/api/admin/employees', data);
      toast.success('Employee added successfully');
      fetchEmployees();
      setShowEmployeeModal(false);
    } catch (e) {
      toast.error('Failed to add employee');
    }
  };

  const handleUpdateEmployee = async (data) => {
    try {
      await adminApi.put(`/api/admin/employees/${editingEmployee._id}`, data);
      toast.success('Employee updated successfully');
      fetchEmployees();
      setShowEmployeeModal(false);
      setEditingEmployee(null);
    } catch (e) {
      toast.error('Failed to update employee');
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await adminApi.delete(`/api/admin/employees/${id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (e) {
      toast.error('Failed to delete employee');
    }
  };

  const handleCreateDepartment = async (data) => {
    try {
      await adminApi.post('/api/admin/departments', data);
      toast.success('Department created successfully');
      fetchDepartments();
      setShowDepartmentModal(false);
    } catch (e) {
      toast.error('Failed to create department');
    }
  };

  const handleUpdateDepartment = async (data) => {
    try {
      await adminApi.put(`/api/admin/departments/${editingDepartment._id}`, data);
      toast.success('Department updated successfully');
      fetchDepartments();
      setShowDepartmentModal(false);
      setEditingDepartment(null);
    } catch (e) {
      toast.error('Failed to update department');
    }
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      await adminApi.delete(`/api/admin/departments/${id}`);
      toast.success('Department deleted');
      fetchDepartments();
    } catch (e) {
      toast.error('Failed to delete department');
    }
  };

  // ========== IMPORT/EXPORT HANDLERS ==========
  
  const handleExportCSV = async () => {
    try {
      const response = await adminApi.get('/api/admin/employees/export/csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employees_export_${getTodayLocal()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Employees exported successfully');
    } catch (error) {
      toast.error('Failed to export employees');
      console.error('Export error:', error);
    }
  };

  const handleImportFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportLoading(true);
    setImportStep('upload');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await adminApi.post('/api/admin/employees/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportPreview(response.data);
      setImportStep('preview');
      
      // Select all valid rows by default
      const validRows = response.data.preview
        .filter(r => r.action === 'create' || r.action === 'update')
        .map(r => r.row_number);
      setSelectedImportRows(validRows);
      
    } catch (error) {
      toast.error('Failed to parse CSV file');
      console.error('Import preview error:', error);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const toggleImportRowSelection = (rowNumber) => {
    setSelectedImportRows(prev => 
      prev.includes(rowNumber) 
        ? prev.filter(r => r !== rowNumber)
        : [...prev, rowNumber]
    );
  };

  const toggleAllImportRows = () => {
    if (!importPreview) return;
    const validRows = importPreview.preview
      .filter(r => r.action === 'create' || r.action === 'update')
      .map(r => r.row_number);
    
    if (selectedImportRows.length === validRows.length) {
      setSelectedImportRows([]);
    } else {
      setSelectedImportRows(validRows);
    }
  };

  const handleApplyImport = async () => {
    if (selectedImportRows.length === 0) {
      toast.error('No rows selected to import');
      return;
    }
    
    setImportLoading(true);
    setImportStep('applying');
    
    try {
      const employeesToProcess = importPreview.preview
        .filter(r => selectedImportRows.includes(r.row_number) && (r.action === 'create' || r.action === 'update'))
        .map(r => ({ action: r.action, data: r.data }));
      
      const response = await adminApi.post('/api/admin/employees/import/apply', {
        employees: employeesToProcess
      });
      
      setImportResults(response.data.results);
      setImportStep('complete');
      fetchEmployees(); // Refresh the list
      
      if (response.data.results.errors.length === 0) {
        toast.success(`Successfully processed ${response.data.results.created + response.data.results.updated} employees`);
      } else {
        toast.warning(`Processed with ${response.data.results.errors.length} errors`);
      }
      
    } catch (error) {
      toast.error('Failed to apply import');
      console.error('Import apply error:', error);
      setImportStep('preview');
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportStep('upload');
    setImportPreview(null);
    setImportResults(null);
    setSelectedImportRows([]);
  };

  const getDepartmentNames = (deptNames) => {
    // Departments are now stored as names, not ObjectIds
    if (!deptNames || deptNames.length === 0) return 'N/A';
    return deptNames.join(', ');
  };

  const getDepartmentEmployees = (dept) => {
    // Match by department name since employees store department names, not IDs
    return employees.filter(emp => 
      emp.departments?.includes(dept.name) || 
      emp.employment?.departments?.includes(dept.name)
    );
  };

  const filteredEmployees = employees.filter(emp =>
    emp.personal?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.work_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.designation?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Organisation</h1>
          <p className="text-neutral-600 mt-1">Manage employees and departments</p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'employees' && (
            <>
              <button 
                onClick={handleExportCSV} 
                className="px-4 py-2 glass-card text-neutral-700 rounded-xl hover:bg-white/60 flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button 
                onClick={() => setShowImportModal(true)} 
                className="px-4 py-2 glass-card text-neutral-700 rounded-xl hover:bg-white/60 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Import CSV
              </button>
              <button onClick={() => { setEditingEmployee(null); setShowEmployeeModal(true); }} className="px-4 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Employee
              </button>
            </>
          )}
          {activeTab === 'departments' && (
            <button onClick={() => { setEditingDepartment(null); setShowDepartmentModal(true); }} className="px-4 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Department
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-white/40">
        <button onClick={() => setActiveTab('employees')} className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'employees' ? 'border-pink-600 text-pink-600' : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'}`}>
          <Users className="w-4 h-4" /> Employees
        </button>
        <button onClick={() => setActiveTab('departments')} className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'departments' ? 'border-pink-600 text-pink-600' : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'}`}>
          <Building2 className="w-4 h-4" /> Departments
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          type="text"
          placeholder={`Search ${activeTab}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-md pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
        />
      </div>

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <div className="soft-card overflow-hidden">
          {/* Mobile card list — swipeable cards on phones (<md) */}
          <div className="md:hidden p-3 space-y-2" data-testid="employees-mobile-list">
            {filteredEmployees.length === 0 ? (
              <div className="py-10 text-center text-sm text-neutral-500">No employees found.</div>
            ) : filteredEmployees.slice(0, 100).map(emp => {
              const levelValue = emp.designation_level || emp.employment?.designation_level || '';
              const levelLabel = designationLevels.find(l => l.value === levelValue)?.label || '';
              const tenure = calculateTenure(emp.employment?.first_working_day || emp.first_working_day);
              const badges = [
                levelLabel ? { label: levelLabel, tone: levelValue === 'founder' ? 'amber' : (levelValue === 'senior' || levelValue === 'associate') ? 'emerald' : 'blue' } : null,
                tenure ? { label: tenure, tone: 'neutral' } : null,
                emp.offboarding_status?.offboarding_completed ? { label: 'Offboarded', tone: 'emerald' } : null,
              ].filter(Boolean);
              return (
                <MobileDataCard
                  key={emp._id}
                  testId={`employee-card-${emp._id}`}
                  title={emp.personal?.full_name || 'N/A'}
                  subtitle={emp.work_email}
                  badges={badges}
                  rows={[
                    { label: 'Dept', value: getDepartmentNames(emp.departments || emp.employment?.departments) || '—' },
                    { label: 'Role', value: emp.designation || emp.employment?.designation || '—' },
                  ]}
                  onTap={() => { setEditingEmployee(emp); setShowEmployeeModal(true); }}
                />
              );
            })}
          </div>
          <div className="hidden md:block">
            <EmployeeDataGrid
              employees={filteredEmployees}
              onEdit={(emp) => { setEditingEmployee(emp); setShowEmployeeModal(true); }}
            />
          </div>
        </div>
      )}

      {/* Departments Tab */}
      {activeTab === 'departments' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDepartments.length === 0 ? (
            <div className="col-span-full glass-card p-12 text-center text-neutral-500">
              No departments found.
            </div>
          ) : (
            filteredDepartments.map(dept => {
              const deptEmployees = getDepartmentEmployees(dept);
              return (
                <div key={dept._id} className="soft-card overflow-hidden">
                  <div className="p-6 border-b border-white/30">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg text-[#1a1a2e]">{dept.name}</h3>
                        <p className="text-sm text-neutral-500 mt-1">{dept.description || 'No description'}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingDepartment(dept); setShowDepartmentModal(true); }} className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteDepartment(dept._id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-medium text-neutral-700 mb-3">{deptEmployees.length} Members</p>
                    <div className="space-y-2">
                      {deptEmployees.slice(0, 5).map(emp => (
                        <div key={emp._id} className="flex items-center gap-3 text-sm">
                          <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                            <span className="text-pink-600 font-medium text-xs">{(emp.personal?.full_name || emp.work_email)?.[0]?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-[#1a1a2e]">{emp.personal?.full_name || emp.work_email}</p>
                            <p className="text-xs text-neutral-500">{emp.designation}</p>
                          </div>
                        </div>
                      ))}
                      {deptEmployees.length > 5 && (
                        <p className="text-sm text-neutral-500">+{deptEmployees.length - 5} more</p>
                      )}
                      {deptEmployees.length === 0 && (
                        <p className="text-sm text-neutral-400">No members yet</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Employee Modal */}
      <EmployeeModal
        isOpen={showEmployeeModal}
        onClose={() => { setShowEmployeeModal(false); setEditingEmployee(null); }}
        onSubmit={editingEmployee ? handleUpdateEmployee : handleCreateEmployee}
        departments={departments}
        existingEmployee={editingEmployee}
        designationLevels={designationLevels}
      />

      {/* Department Modal */}
      <DepartmentModal
        isOpen={showDepartmentModal}
        onClose={() => { setShowDepartmentModal(false); setEditingDepartment(null); }}
        onSubmit={editingDepartment ? handleUpdateDepartment : handleCreateDepartment}
        employees={employees}
        existingDepartment={editingDepartment}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => { setShowResetPasswordModal(false); setResetPasswordEmployee(null); }}
        employee={resetPasswordEmployee}
        onSuccess={() => fetchEmployees()}
      />

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white  shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Upload className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Import Employees from CSV</h2>
                  <p className="text-pink-100 text-sm">
                    {importStep === 'upload' && 'Upload a CSV file to import employees'}
                    {importStep === 'preview' && 'Review changes before applying'}
                    {importStep === 'applying' && 'Applying changes...'}
                    {importStep === 'complete' && 'Import complete'}
                  </p>
                </div>
              </div>
              <button onClick={closeImportModal} className="p-2 hover:bg-white/20 rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Upload Step */}
              {importStep === 'upload' && (
                <div className="text-center py-12">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImportFileSelect}
                    accept=".csv"
                    className="hidden"
                  />
                  <div className="w-20 h-20 mx-auto mb-6 bg-pink-100  flex items-center justify-center">
                    <FileText className="w-10 h-10 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">Select CSV File</h3>
                  <p className="text-neutral-500 mb-6 max-w-md mx-auto">
                    Upload a CSV file with employee data. The system will automatically detect new employees and updates to existing ones.
                  </p>
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importLoading}
                    className="px-6 py-3 bg-pink-600 text-white rounded-xl hover:bg-pink-700 font-medium inline-flex items-center gap-2"
                  >
                    {importLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Choose File
                      </>
                    )}
                  </button>

                  <div className="mt-8 p-4 bg-white/40 rounded-xl text-left max-w-lg mx-auto">
                    <h4 className="font-semibold text-[#1a1a2e] mb-2">CSV Format</h4>
                    <p className="text-sm text-neutral-600 mb-2">Required columns: <code className="bg-neutral-200 px-1 rounded">employee_id</code>, <code className="bg-neutral-200 px-1 rounded">work_email</code></p>
                    <p className="text-sm text-neutral-600">
                      <button onClick={handleExportCSV} className="text-pink-600 hover:underline">Download current employee list</button> to see the correct format and all available fields.
                    </p>
                  </div>
                </div>
              )}

              {/* Preview Step */}
              {importStep === 'preview' && importPreview && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white/40 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-[#1a1a2e]">{importPreview.summary.total}</p>
                      <p className="text-sm text-neutral-600">Total Rows</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{importPreview.summary.to_create}</p>
                      <p className="text-sm text-green-700">New Employees</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{importPreview.summary.to_update}</p>
                      <p className="text-sm text-blue-700">To Update</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{importPreview.summary.errors}</p>
                      <p className="text-sm text-red-700">Errors</p>
                    </div>
                  </div>

                  {/* Preview Table */}
                  <div className="border border-white/40 rounded-xl overflow-hidden">
                    <div className="bg-white/40 px-4 py-3 border-b border-white/40 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedImportRows.length === importPreview.preview.filter(r => r.action === 'create' || r.action === 'update').length}
                          onChange={toggleAllImportRows}
                          className="w-4 h-4 rounded border-white/40 text-pink-600 focus:ring-pink-500"
                        />
                        <span className="text-sm font-medium text-neutral-700">
                          {selectedImportRows.length} of {importPreview.preview.filter(r => r.action === 'create' || r.action === 'update').length} selected
                        </span>
                      </div>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-white/40 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left w-12"></th>
                            <th className="px-4 py-2 text-left">Row</th>
                            <th className="px-4 py-2 text-left">Action</th>
                            <th className="px-4 py-2 text-left">Employee ID</th>
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Email</th>
                            <th className="px-4 py-2 text-left">Department</th>
                            <th className="px-4 py-2 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/30">
                          {importPreview.preview.map((row) => (
                            <tr 
                              key={row.row_number} 
                              className={`${row.action === 'error' ? 'bg-red-50' : selectedImportRows.includes(row.row_number) ? 'bg-pink-50' : ''}`}
                            >
                              <td className="px-4 py-2">
                                {row.action !== 'error' && (
                                  <input
                                    type="checkbox"
                                    checked={selectedImportRows.includes(row.row_number)}
                                    onChange={() => toggleImportRowSelection(row.row_number)}
                                    className="w-4 h-4 rounded border-white/40 text-pink-600 focus:ring-pink-500"
                                  />
                                )}
                              </td>
                              <td className="px-4 py-2 text-neutral-500">{row.row_number}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  row.action === 'create' ? 'bg-green-100 text-green-700' :
                                  row.action === 'update' ? 'bg-blue-100 text-blue-700' :
                                  row.action === 'error' ? 'bg-red-100 text-red-700' :
                                  'bg-neutral-100 text-neutral-700'
                                }`}>
                                  {row.action === 'create' ? 'New' : row.action === 'update' ? 'Update' : row.action === 'error' ? 'Error' : 'Skip'}
                                </span>
                              </td>
                              <td className="px-4 py-2 font-medium">{row.data.employee_id || '-'}</td>
                              <td className="px-4 py-2">{row.data.full_name || `${row.data.first_name} ${row.data.last_name}`.trim() || '-'}</td>
                              <td className="px-4 py-2">{row.data.work_email || '-'}</td>
                              <td className="px-4 py-2">{row.data.departments?.join(', ') || '-'}</td>
                              <td className="px-4 py-2">
                                {row.errors.length > 0 && (
                                  <div className="flex items-center gap-1 text-red-600">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="text-xs">{row.errors[0]}</span>
                                  </div>
                                )}
                                {row.warnings.length > 0 && row.errors.length === 0 && (
                                  <div className="text-amber-600 text-xs">{row.warnings[0]}</div>
                                )}
                                {row.errors.length === 0 && row.warnings.length === 0 && (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Applying Step */}
              {importStep === 'applying' && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 relative">
                    <div className="absolute inset-0 rounded-full border-4 border-pink-100"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-pink-600 border-t-transparent animate-spin"></div>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">Applying Changes</h3>
                  <p className="text-neutral-500">Processing {selectedImportRows.length} employees...</p>
                </div>
              )}

              {/* Complete Step */}
              {importStep === 'complete' && importResults && (
                <div className="text-center py-8">
                  <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">Import Complete</h3>
                  
                  <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mt-6">
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-green-600">{importResults.created}</p>
                      <p className="text-sm text-green-700">Created</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-blue-600">{importResults.updated}</p>
                      <p className="text-sm text-blue-700">Updated</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-red-600">{importResults.errors.length}</p>
                      <p className="text-sm text-red-700">Errors</p>
                    </div>
                  </div>

                  {importResults.errors.length > 0 && (
                    <div className="mt-6 p-4 bg-red-50 rounded-xl text-left max-w-lg mx-auto">
                      <h4 className="font-semibold text-red-900 mb-2">Errors:</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        {importResults.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/40 bg-white/40 flex items-center justify-between flex-shrink-0">
              <button
                onClick={closeImportModal}
                className="px-4 py-2 text-neutral-700 hover:text-[#1a1a2e]"
              >
                {importStep === 'complete' ? 'Close' : 'Cancel'}
              </button>
              
              {importStep === 'preview' && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { setImportStep('upload'); setImportPreview(null); }}
                    className="px-4 py-2 border border-white/40 rounded-xl text-neutral-700 hover:bg-neutral-100"
                  >
                    Upload Different File
                  </button>
                  <button
                    onClick={handleApplyImport}
                    disabled={selectedImportRows.length === 0 || importLoading}
                    className="px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {importLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Apply {selectedImportRows.length} Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Offboarding Wizard */}
      <OffboardingWizard
        employee={offboardingEmployee}
        isOpen={showOffboardingWizard}
        onClose={() => {
          setShowOffboardingWizard(false);
          setOffboardingEmployee(null);
        }}
        onComplete={() => {
          fetchEmployees();
          toast.success('Employee offboarding completed!');
        }}
      />
    </div>
  );
}
